import {
	ApartmentOutlined,
	CheckCircleOutlined,
	ManOutlined,
	StopOutlined,
	TeamOutlined,
	UserOutlined,
	WomanOutlined,
} from '@ant-design/icons'
import { Card, Col, Progress, Row, Spin, Statistic, Tag, Tooltip, Typography } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { staffService } from '../../common/service/staff.service'
import { useThemeColors } from '../../common/ThemeContext'

interface StaffDashboardProps {}

const CARD_STYLE: React.CSSProperties = { borderRadius: 10, height: '100%' }

const StaffDashboard: React.FC<StaffDashboardProps> = () => {
	const themeColors = useThemeColors()
	const [data, setData] = useState<any[]>([])
	const [dashLoading, setDashLoading] = useState(true)

	useEffect(() => {
		const fetchAll = async () => {
			try {
				const res: any = await staffService.fetchListing({ limit: 1000 })
				if (res?.statusCode === 200) {
					setData(Array.isArray(res.data) ? res.data : [])
				}
			} catch {}
			setDashLoading(false)
		}
		fetchAll()
	}, [])
	const stats = useMemo(() => {
		const total = data.length
		const active = data.filter((s) => (s.status || '').toLowerCase() === 'active').length
		const inactive = data.filter((s) => (s.status || '').toLowerCase() === 'inactive').length
		const male = data.filter((s) => s.sex === 'male').length
		const female = data.filter((s) => s.sex === 'female').length

		// By department
		const deptMap: Record<string, number> = {}
		for (const s of data) {
			const d = s.departmentId || 'Unassigned'
			deptMap[d] = (deptMap[d] || 0) + 1
		}
		const departments = Object.entries(deptMap)
			.map(([name, count]) => ({ name, count }))
			.sort((a, b) => b.count - a.count)

		// By ranking
		const rankingMap: Record<string, number> = {
			executor: 0,
			manager_level_1: 0,
			manager_level_2: 0,
			manager_level_3: 0,
			other: 0,
		}
		for (const s of data) {
			const r = s.ranking as string | undefined
			if (r === 'executor') rankingMap.executor++
			else if (r === 'manager_level_1') rankingMap.manager_level_1++
			else if (r === 'manager_level_2') rankingMap.manager_level_2++
			else if (r === 'manager_level_3') rankingMap.manager_level_3++
			else rankingMap.other++
		}

		// Manager assignment
		const withManager = data.filter((s) => s.firstLineManager?.id).length
		const withoutManager = total - withManager

		return {
			total,
			active,
			inactive,
			male,
			female,
			departments,
			rankingMap,
			withManager,
			withoutManager,
		}
	}, [data])

	const pct = (n: number) => (stats.total > 0 ? Math.round((n / stats.total) * 100) : 0)

	if (dashLoading) {
		return (
			<div style={{ textAlign: 'center', padding: 48 }}>
				<Spin size='large' />
			</div>
		)
	}

	return (
		<div style={{ padding: '8px 0 16px' }}>
			<Typography.Title level={3} style={{ marginBottom: 4 }}>
				<TeamOutlined /> Staff Dashboard
			</Typography.Title>
			<Typography.Text type='secondary' style={{ display: 'block', marginBottom: 20 }}>
				Overview of {stats.total} staff member{stats.total !== 1 ? 's' : ''} across all
				departments.
			</Typography.Text>

			{/* ── Row 1: Key Counts ── */}
			<Row gutter={[16, 16]}>
				<Col xs={12} sm={8} md={4}>
					<Card style={CARD_STYLE}>
						<Statistic
							title='Total Staff'
							value={stats.total}
							prefix={<UserOutlined />}
							valueStyle={{ color: '#1677ff' }}
						/>
					</Card>
				</Col>
				<Col xs={12} sm={8} md={4}>
					<Card style={CARD_STYLE}>
						<Statistic
							title='Active'
							value={stats.active}
							prefix={<CheckCircleOutlined />}
							valueStyle={{ color: '#52c41a' }}
							suffix={
								<span style={{ fontSize: 12, color: themeColors.textTertiary }}>
									{' '}
									({pct(stats.active)}%)
								</span>
							}
						/>
					</Card>
				</Col>
				<Col xs={12} sm={8} md={4}>
					<Card style={CARD_STYLE}>
						<Statistic
							title='Inactive'
							value={stats.inactive}
							prefix={<StopOutlined />}
							valueStyle={{ color: stats.inactive > 0 ? '#ff4d4f' : '#8c8c8c' }}
							suffix={
								<span style={{ fontSize: 12, color: themeColors.textTertiary }}>
									{' '}
									({pct(stats.inactive)}%)
								</span>
							}
						/>
					</Card>
				</Col>
				<Col xs={12} sm={8} md={4}>
					<Card style={CARD_STYLE}>
						<Statistic
							title='Male'
							value={stats.male}
							prefix={<ManOutlined />}
							valueStyle={{ color: '#1677ff' }}
							suffix={
								<span style={{ fontSize: 12, color: themeColors.textTertiary }}>
									{' '}
									({pct(stats.male)}%)
								</span>
							}
						/>
					</Card>
				</Col>
				<Col xs={12} sm={8} md={4}>
					<Card style={CARD_STYLE}>
						<Statistic
							title='Female'
							value={stats.female}
							prefix={<WomanOutlined />}
							valueStyle={{ color: '#eb2f96' }}
							suffix={
								<span style={{ fontSize: 12, color: themeColors.textTertiary }}>
									{' '}
									({pct(stats.female)}%)
								</span>
							}
						/>
					</Card>
				</Col>
				<Col xs={12} sm={8} md={4}>
					<Card style={CARD_STYLE}>
						<Statistic
							title='No Line Manager'
							value={stats.withoutManager}
							prefix={<ApartmentOutlined />}
							valueStyle={{
								color: stats.withoutManager > 0 ? '#faad14' : '#52c41a',
							}}
							suffix={
								<span style={{ fontSize: 12, color: themeColors.textTertiary }}>
									{' '}
									({pct(stats.withoutManager)}%)
								</span>
							}
						/>
					</Card>
				</Col>
			</Row>

			{/* ── Row 2: Department Breakdown + Ranking ── */}
			<Row gutter={[16, 16]} style={{ marginTop: 16 }}>
				<Col xs={24} md={14}>
					<Card title='Staff by Department' style={CARD_STYLE}>
						{stats.departments.length === 0 ? (
							<Typography.Text type='secondary'>No data</Typography.Text>
						) : (
							<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
								{stats.departments.map(({ name, count }) => (
									<div key={name}>
										<div
											style={{
												display: 'flex',
												justifyContent: 'space-between',
												marginBottom: 2,
											}}
										>
											<Typography.Text
												ellipsis
												style={{ maxWidth: '65%' }}
												title={name}
											>
												{name}
											</Typography.Text>
											<Typography.Text type='secondary'>
												{count} ({pct(count)}%)
											</Typography.Text>
										</div>
										<Progress
											percent={pct(count)}
											showInfo={false}
											size='small'
											strokeColor='#1677ff'
										/>
									</div>
								))}
							</div>
						)}
					</Card>
				</Col>
				<Col xs={24} md={10}>
					<Card title='Staff by Ranking' style={CARD_STYLE}>
						<div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
							{[
								{
									key: 'executor',
									label: 'Executor',
									color: '#722ed1',
									count: stats.rankingMap.executor,
								},
								{
									key: 'manager_level_1',
									label: 'Manager Level 1',
									color: '#1677ff',
									count: stats.rankingMap.manager_level_1,
								},
								{
									key: 'manager_level_2',
									label: 'Manager Level 2',
									color: '#13c2c2',
									count: stats.rankingMap.manager_level_2,
								},
								{
									key: 'manager_level_3',
									label: 'Manager Level 3',
									color: '#9333ea',
									count: stats.rankingMap.manager_level_3,
								},
								{
									key: 'other',
									label: 'Unranked',
									color: '#8c8c8c',
									count: stats.rankingMap.other,
								},
							].map(({ key, label, color, count }) => (
								<div key={key}>
									<div
										style={{
											display: 'flex',
											justifyContent: 'space-between',
											marginBottom: 2,
										}}
									>
										<Tooltip title={label}>
											<Tag color={color} style={{ cursor: 'default' }}>
												{label}
											</Tag>
										</Tooltip>
										<Typography.Text type='secondary'>
											{count} ({pct(count)}%)
										</Typography.Text>
									</div>
									<Progress
										percent={pct(count)}
										showInfo={false}
										size='small'
										strokeColor={color}
									/>
								</div>
							))}
						</div>
					</Card>
				</Col>
			</Row>

			{/* ── Row 3: Manager Assignment ── */}
			<Row gutter={[16, 16]} style={{ marginTop: 16 }}>
				<Col xs={24} md={12}>
					<Card title='Manager Assignment' style={CARD_STYLE}>
						<Row gutter={16}>
							<Col span={12} style={{ textAlign: 'center' }}>
								<Statistic
									title='With 1st Line Manager'
									value={stats.withManager}
									valueStyle={{ color: '#52c41a', fontSize: 28 }}
								/>
								<Progress
									percent={pct(stats.withManager)}
									size='small'
									strokeColor='#52c41a'
									style={{ marginTop: 8 }}
								/>
							</Col>
							<Col span={12} style={{ textAlign: 'center' }}>
								<Statistic
									title='Without Manager'
									value={stats.withoutManager}
									valueStyle={{
										color: stats.withoutManager > 0 ? '#faad14' : '#52c41a',
										fontSize: 28,
									}}
								/>
								<Progress
									percent={pct(stats.withoutManager)}
									size='small'
									strokeColor={stats.withoutManager > 0 ? '#faad14' : '#52c41a'}
									style={{ marginTop: 8 }}
								/>
							</Col>
						</Row>
					</Card>
				</Col>
				<Col xs={24} md={12}>
					<Card title='Gender Distribution' style={CARD_STYLE}>
						<Row gutter={16}>
							<Col span={12} style={{ textAlign: 'center' }}>
								<Statistic
									title='Male'
									value={stats.male}
									valueStyle={{ color: '#1677ff', fontSize: 28 }}
									prefix={<ManOutlined />}
								/>
								<Progress
									percent={pct(stats.male)}
									size='small'
									strokeColor='#1677ff'
									style={{ marginTop: 8 }}
								/>
							</Col>
							<Col span={12} style={{ textAlign: 'center' }}>
								<Statistic
									title='Female'
									value={stats.female}
									valueStyle={{ color: '#eb2f96', fontSize: 28 }}
									prefix={<WomanOutlined />}
								/>
								<Progress
									percent={pct(stats.female)}
									size='small'
									strokeColor='#eb2f96'
									style={{ marginTop: 8 }}
								/>
							</Col>
						</Row>
					</Card>
				</Col>
			</Row>
		</div>
	)
}

export default StaffDashboard
